/* ==========================================================================
   RepuestosYA — motor de revelado
   Reveal-on-scroll, escalonado, contadores y dibujo de SVG.
   Un IntersectionObserver compartido, respeto total a
   prefers-reduced-motion, y failsafe si el observer nunca dispara.
   ========================================================================== */
(function () {
  'use strict';

  document.documentElement.classList.add('rya-js');

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var anyRevealed = false;
  var io = null;

  function revealNow(el) {
    anyRevealed = true;
    el.classList.add('is-in');
  }

  function revealAllPending() {
    var pending = document.querySelectorAll('.rya-reveal:not(.is-in)');
    for (var i = 0; i < pending.length; i++) pending[i].classList.add('is-in');
  }

  /* Failsafe: si a los 3s de estar visible no se reveló nada,
     el observer no está funcionando — mostramos todo. */
  function armFailsafe() {
    setTimeout(function () {
      if (anyRevealed) return;
      if (document.visibilityState === 'hidden') {
        document.addEventListener('visibilitychange', function onVis() {
          document.removeEventListener('visibilitychange', onVis);
          armFailsafe();
        });
        return;
      }
      revealAllPending();
    }, 3000);
  }

  function ensureObserver() {
    if (io || !('IntersectionObserver' in window)) return io;
    io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            revealNow(entries[i].target);
            io.unobserve(entries[i].target);
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    return io;
  }

  function initReveals(root) {
    var scope = root || document;

    var groups = scope.querySelectorAll('[data-rya-stagger]:not([data-rya-ready])');
    for (var g = 0; g < groups.length; g++) {
      groups[g].setAttribute('data-rya-ready', '1');
      var step = parseFloat(groups[g].getAttribute('data-rya-stagger')) || 0.06;
      var kids = groups[g].children;
      for (var k = 0; k < kids.length; k++) {
        kids[k].classList.add('rya-reveal');
        kids[k].style.setProperty('--rya-d', (k * step).toFixed(2) + 's');
      }
    }

    var targets = scope.querySelectorAll('.rya-reveal:not(.is-in)');
    if (reducedMotion || !ensureObserver()) {
      for (var i = 0; i < targets.length; i++) revealNow(targets[i]);
      return;
    }
    for (var j = 0; j < targets.length; j++) io.observe(targets[j]);
  }

  /* ---------- Contadores ---------- */
  function animateCount(el) {
    var target = parseFloat(el.getAttribute('data-rya-count-to')) || 0;
    var suffix = el.getAttribute('data-rya-count-suffix') || '';
    var duration = parseInt(el.getAttribute('data-rya-count-ms'), 10) || 1400;

    if (reducedMotion) {
      el.textContent = target.toLocaleString('es-HN') + suffix;
      return;
    }

    var start = null;
    function frame(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / duration, 1);
      var eased = 1 - Math.pow(1 - p, 4);
      el.textContent = Math.round(target * eased).toLocaleString('es-HN') + suffix;
      if (p < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  function finishCounter(el) {
    var target = parseFloat(el.getAttribute('data-rya-count-to')) || 0;
    var suffix = el.getAttribute('data-rya-count-suffix') || '';
    el.textContent = target.toLocaleString('es-HN') + suffix;
  }

  function initCounters(root) {
    var scope = root || document;
    var els = scope.querySelectorAll('[data-rya-count-to]:not([data-rya-ready])');
    if (!els.length) return;

    if (!('IntersectionObserver' in window)) {
      for (var i = 0; i < els.length; i++) {
        els[i].setAttribute('data-rya-ready', '1');
        animateCount(els[i]);
      }
      return;
    }

    var cio = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            animateCount(entries[i].target);
            cio.unobserve(entries[i].target);
          }
        }
      },
      { threshold: 0.4 }
    );

    for (var j = 0; j < els.length; j++) {
      els[j].setAttribute('data-rya-ready', '1');
      cio.observe(els[j]);
    }

    /* Mismo failsafe que los reveals: nunca dejar un "0" pegado. */
    setTimeout(function () {
      Array.prototype.forEach.call(els, function (el) {
        var raw = parseFloat((el.textContent || '').replace(/[^0-9.]/g, ''));
        if (!raw) finishCounter(el);
      });
    }, 6000);
  }

  /* ---------- Dibujo de SVG (plano técnico) ---------- */
  function initDraw(root) {
    var scope = root || document;
    var svgs = scope.querySelectorAll('[data-rya-draw]:not([data-rya-ready])');
    if (!svgs.length) return;

    Array.prototype.forEach.call(svgs, function (svg) {
      svg.setAttribute('data-rya-ready', '1');
      var paths = svg.querySelectorAll('path, circle, line, polyline, ellipse');

      Array.prototype.forEach.call(paths, function (p) {
        var len = 0;
        try {
          len = p.getTotalLength();
        } catch (e) {
          return;
        }
        p.style.strokeDasharray = len + ' ' + len;
        p.style.strokeDashoffset = reducedMotion ? '0' : String(len);
      });

      if (reducedMotion) {
        svg.classList.add('is-drawn');
        return;
      }

      var started = false;
      function start() {
        if (started) return;
        started = true;
        Array.prototype.forEach.call(paths, function (p, idx) {
          p.style.transition =
            'stroke-dashoffset 1.4s cubic-bezier(.45,0,.2,1) ' + (idx * 0.1).toFixed(2) + 's';
          p.style.strokeDashoffset = '0';
        });
        svg.classList.add('is-drawn');
      }

      if (!('IntersectionObserver' in window)) {
        start();
        return;
      }

      var dio = new IntersectionObserver(
        function (entries) {
          if (entries[0].isIntersecting) {
            start();
            dio.disconnect();
          }
        },
        { threshold: 0.25 }
      );
      dio.observe(svg);

      setTimeout(start, 5000);
    });
  }

  /* ---------- Arranque ---------- */
  function initAll(root) {
    initReveals(root);
    initCounters(root);
    initDraw(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
      armFailsafe();
    });
  } else {
    initAll(document);
    armFailsafe();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target || document);
  });

  window.RYACinematic = { refresh: initAll };
})();
